/* Performance Analytics — the new (v8) interactive dashboards.
 * Ported 1:1 from the alpaca_performance (8) design: Executive KPI, Missed
 * Recurrence Detector, Job TAT Dashboard, Due Date Tracking, Missed TAT Alerts,
 * and the three Revenue dashboards (Explorer / Trend & Forecast / Concentration).
 * Demo data only — no API wiring. */
import { useMemo, useState } from 'react';
import { PAChart } from './PAChart';
import { ChartPanel, DataTable } from './parts';
import type { Cell } from './types';
import {
  CLIENTS, CLIENT_NAMES, TESTS, REPS, MONTHS, WEEKS12, PALETTE as C, series, one,
  DEPARTMENTS, MATRICES, MATRIX_NAMES, PROGRAMS, SAMPLE_TYPES, PROJECTS, CLIENT_REVENUE_WEIGHT,
} from './mockData';

/* ── shared bits ── */
type Dir = 'up' | 'down' | 'flat';
interface KpiC { label: string; value: string; delta?: string; dir?: Dir; valueColor?: string; }
function KpiRowN({ kpis, cols = 4 }: { kpis: KpiC[]; cols?: number }) {
  return (
    <div className="pa-kpi-row" style={cols !== 4 ? { gridTemplateColumns: `repeat(${cols},1fr)` } : undefined}>
      {kpis.map((k, i) => (
        <div className="pa-kpi-card" key={i}>
          <div className="pa-kpi-label">{k.label}</div>
          <div className="pa-kpi-value" style={k.valueColor ? { color: k.valueColor } : undefined}>{k.value}</div>
          {k.delta && <div className={`pa-kpi-delta ${k.dir ?? 'flat'}`}>{k.delta}</div>}
        </div>
      ))}
    </div>
  );
}
function PageHead({ title, desc, badge }: { title: string; desc: string; badge?: 'new' | 'ai' }) {
  return (
    <>
      <div className="pa-page-title">{title}{badge && <span className={`pa-badge ${badge}`}>{badge === 'ai' ? 'AI' : 'New'}</span>}</div>
      <div className="pa-page-desc">{desc}</div>
    </>
  );
}
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtDate = (d: Date) => `${MON[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
const addDays = (date: Date, days: number) => { const d = new Date(date); d.setDate(d.getDate() + days); return d; };
const daysBetween = (a: Date, b: Date) => Math.round((b.getTime() - a.getTime()) / 86400000);

/* ════════════════════ Executive KPI Dashboard ════════════════════ */
export function ExecKpiPage() {
  return (
    <>
      <PageHead title="Executive KPI Dashboard"
        desc="The six numbers that matter most at the company level — volume, operational health, quality, and retention in one view. Sample Backlog sits alongside Revenue and TAT here because it deserves the same visibility, not three clicks deep in a sub-menu." />
      <KpiRowN cols={3} kpis={[
        { label: 'Revenue (MTD)', value: '$187K', delta: '▲ 6.4% vs prior month', dir: 'up' },
        { label: 'Samples Received (MTD)', value: '1,842', delta: '▲ 6.2% vs prior period', dir: 'up' },
        { label: 'Sample Backlog', value: '312', delta: '▲ 14 vs last week', dir: 'down' },
      ]} />
      <KpiRowN cols={3} kpis={[
        { label: 'Average TAT', value: '4.6 days', delta: '▲ 0.3d vs target', dir: 'up' },
        { label: 'QC Pass Rate', value: '96.8%', delta: '▲ 0.4pp', dir: 'up' },
        { label: 'Client Retention', value: '94.1%', delta: '— stable', dir: 'flat' },
      ]} />
      <div className="pa-panel-row pa-cols-2e">
        <ChartPanel panel={{ title: 'Revenue — 12 month trend', tall: true,
          chart: { type: 'bar', labels: MONTHS, data: series(12, 170000, 40000, 5), color: C.orange },
          note: 'Monthly revenue across all clients, all departments combined.' }} />
        <ChartPanel panel={{ title: 'Sample Backlog — 12 week trend', tall: true,
          chart: { type: 'line', labels: WEEKS12, series: [{ color: C.red, fill: true, data: series(12, 280, 40, 6) }] },
          note: 'Total open samples at end of each week. A rising line means intake is outpacing completion.' }} />
      </div>
    </>
  );
}

/* ════════════════════ Missed Recurrence Detector ════════════════════ */
const TODAY = new Date(2026, 5, 19);
interface RecurRule { id: string; client: string; clientName: string; pattern: string; freqDays: number; endDate: Date; startDate: Date; brokenAfterOccurrence: number | null; value: number; }
const RECUR_RULES: RecurRule[] = [
  { id: 'CR260227010', client: 'COA', clientName: 'City of Austin', pattern: 'Monthly — last Friday', freqDays: 30, endDate: new Date(2030, 11, 27), startDate: new Date(2025, 1, 28), brokenAfterOccurrence: 4, value: 48000 },
  { id: 'CR250228042', client: 'DWO', clientName: 'City of Houston (DWO)', pattern: 'Weekly — every Tuesday', freqDays: 7, endDate: new Date(2028, 5, 30), startDate: new Date(2025, 1, 28), brokenAfterOccurrence: null, value: 62000 },
  { id: 'CR250530046', client: 'AQT', clientName: 'Aqua Tech Labs', pattern: 'Monthly — 1st & 2nd Wed', freqDays: 14, endDate: new Date(2029, 2, 1), startDate: new Date(2025, 4, 30), brokenAfterOccurrence: 6, value: 31000 },
  { id: 'CR250829037', client: 'HHD', clientName: 'Hill Hollow District', pattern: 'Daily — weekdays', freqDays: 1, endDate: new Date(2027, 11, 31), startDate: new Date(2025, 7, 29), brokenAfterOccurrence: null, value: 54000 },
  { id: 'CR251128043', client: 'BAT', clientName: 'Bay Area Treatment', pattern: 'Monthly — last Friday', freqDays: 30, endDate: new Date(2029, 6, 1), startDate: new Date(2025, 10, 28), brokenAfterOccurrence: null, value: 27000 },
  { id: 'CR260227011', client: 'RRA', clientName: 'Rio Reclamation Authority', pattern: 'Quarterly', freqDays: 91, endDate: new Date(2031, 0, 1), startDate: new Date(2025, 1, 27), brokenAfterOccurrence: 2, value: 38000 },
  { id: 'CR250115022', client: 'WCO', clientName: 'Westcoast Water Co.', pattern: 'Weekly — every Monday', freqDays: 7, endDate: new Date(2027, 11, 31), startDate: new Date(2025, 0, 15), brokenAfterOccurrence: null, value: 22000 },
  { id: 'CR250410018', client: 'JEL', clientName: 'Jenkins Environmental', pattern: 'Monthly — 15th', freqDays: 30, endDate: new Date(2028, 3, 10), startDate: new Date(2025, 3, 10), brokenAfterOccurrence: null, value: 19000 },
];
interface Occ { date: Date; occNum: number; fired: boolean; due: boolean; }
function buildRecurrenceTimeline(rule: RecurRule): Occ[] {
  const occ: Occ[] = []; let occDate = new Date(rule.startDate); let occNum = 0;
  while (occDate <= TODAY && occDate <= rule.endDate) {
    occNum++;
    const fired = rule.brokenAfterOccurrence === null || occNum <= rule.brokenAfterOccurrence;
    occ.push({ date: new Date(occDate), occNum, fired, due: occDate <= TODAY });
    occDate = addDays(occDate, rule.freqDays);
  }
  if (occDate <= rule.endDate) occ.push({ date: new Date(occDate), occNum: occNum + 1, fired: false, due: false });
  return occ;
}
function analyzeRecurrence(rule: RecurRule) {
  const timeline = buildRecurrenceTimeline(rule);
  const dueOcc = timeline.filter((o) => o.due);
  const lastDue = dueOcc[dueOcc.length - 1]!;
  const lastFired = [...timeline].reverse().find((o) => o.fired);
  const missedCount = dueOcc.filter((o) => !o.fired).length;
  let consecutiveMisses = 0;
  for (let i = dueOcc.length - 1; i >= 0; i--) { if (!dueOcc[i]!.fired) consecutiveMisses++; else break; }
  const isMissing = consecutiveMisses > 0;
  const daysSinceShouldHaveFired = isMissing ? daysBetween(lastDue.date, TODAY) : 0;
  const severity: 'ok' | 'warn' | 'critical' = isMissing && consecutiveMisses >= 2 ? 'critical' : isMissing ? 'warn' : 'ok';
  return { rule, timeline, lastDue, lastFired, missedCount, consecutiveMisses, isMissing, daysSinceShouldHaveFired, severity };
}
type RecurAnalysis = ReturnType<typeof analyzeRecurrence>;

function TimelineRow({ a }: { a: RecurAnalysis }) {
  const recent = a.timeline.slice(-6);
  const minDate = recent[0]!.date.getTime();
  const maxDate = Math.max(TODAY.getTime(), recent[recent.length - 1]!.date.getTime());
  const span = maxDate - minDate || 1;
  const todayPct = ((TODAY.getTime() - minDate) / span) * 100;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', fontSize: 11.5, marginBottom: 2 }}>
        <span className={`pa-severity-dot ${a.severity}`} /><b>{a.rule.id}</b>&nbsp;
        <span style={{ color: 'var(--pa-text-light)' }}>— {a.rule.clientName} ({a.rule.client}) · {a.rule.pattern}</span>
        {a.isMissing
          ? <span className="pa-pill red" style={{ marginLeft: 'auto' }}>{a.consecutiveMisses} missed in a row</span>
          : <span className="pa-pill green" style={{ marginLeft: 'auto' }}>On schedule</span>}
      </div>
      <div className="pa-timeline-track">
        <div className="pa-timeline-now" style={{ left: `${todayPct}%` }} />
        {recent.map((o, i) => {
          const pct = ((o.date.getTime() - minDate) / span) * 100;
          const cls = !o.due ? 'expected' : o.fired ? 'fired' : 'missed';
          return <div key={i} className={`pa-timeline-marker ${cls}`} style={{ left: `${pct}%` }}
            title={`${fmtDate(o.date)} — ${!o.due ? 'upcoming' : o.fired ? 'fired on schedule' : 'MISSED — no event generated'}`} />;
        })}
      </div>
    </div>
  );
}

export function RecurrencePage() {
  const v = useMemo(() => {
    const analyses = RECUR_RULES.map(analyzeRecurrence).sort((a, b) => {
      const r = { critical: 0, warn: 1, ok: 2 } as const;
      return r[a.severity] !== r[b.severity] ? r[a.severity] - r[b.severity] : b.consecutiveMisses - a.consecutiveMisses;
    });
    const missing = analyses.filter((a) => a.isMissing);
    const critical = analyses.filter((a) => a.severity === 'critical');
    const revenueAtRisk = missing.reduce((s, a) => s + a.rule.value, 0);
    const worst = analyses[0]!;
    const avgOverdue = Math.round(missing.reduce((s, a) => s + a.daysSinceShouldHaveFired, 0) / Math.max(1, missing.length));
    return { analyses, missing, critical, revenueAtRisk, worst, avgOverdue };
  }, []);

  const tableRows: Cell[][] = v.analyses.map((a) => {
    const status = a.severity === 'critical' ? 'Critical — repeated miss' : a.severity === 'warn' ? 'Missed' : 'Healthy';
    const pill = a.severity === 'critical' ? 'red' : a.severity === 'warn' ? 'amber' : 'green';
    return [a.rule.id, `${a.rule.clientName} (${a.rule.client})`, a.rule.pattern, fmtDate(a.rule.endDate),
      fmtDate(a.lastDue.date), a.lastFired ? fmtDate(a.lastFired.date) : 'Never fired', { text: status, pill } as Cell];
  });

  return (
    <>
      <PageHead title="Missed Recurrence Detector" badge="new"
        desc="Checks every active client request recurrence against its own schedule — Daily, Weekly, Monthly, Yearly — and flags any rule that should have generated a request event by now but didn't. Built directly off recurring tickets like CR260227010 (monthly bottle delivery recurrence that silently stopped firing despite an end date of 12/27/2030)." />

      {v.missing.length > 0 ? (
        <div className="pa-recur-banner">
          <div className="pa-recur-banner-icon">⚠️</div>
          <div className="pa-recur-banner-text"><b>{v.missing.length} active recurrence{v.missing.length > 1 ? 's' : ''} {v.missing.length > 1 ? 'have' : 'has'} stopped firing.</b>
            {v.critical.length > 0 && ` ${v.critical.length} of these ${v.critical.length > 1 ? 'have' : 'has'} missed 2 or more occurrences in a row — the same silent-failure pattern reported for CR260227010.`}
            {' '}Review the table below before a client notices a missed delivery.</div>
        </div>
      ) : (
        <div className="pa-recur-banner clear"><div className="pa-recur-banner-icon">✅</div>
          <div className="pa-recur-banner-text"><b>All active recurrences are firing on schedule.</b> No gaps detected against expected occurrence dates.</div></div>
      )}

      <KpiRowN kpis={[
        { label: 'Active Recurrences', value: String(v.analyses.length), delta: '— monitored', dir: 'flat' },
        { label: 'Currently Missing Fires', value: String(v.missing.length), delta: `${v.critical.length} critical`, dir: v.missing.length > 0 ? 'down' : 'up' },
        { label: 'Revenue Exposure', value: `$${Math.round(v.revenueAtRisk / 1000)}K`, delta: 'annualized, affected rules', dir: 'down' },
        { label: 'Worst Gap', value: v.worst.isMissing ? v.worst.rule.id : '—', delta: v.worst.isMissing ? `${v.worst.daysSinceShouldHaveFired}d since expected fire` : 'none', dir: 'down' },
      ]} />

      <div className="pa-panel">
        <div className="pa-panel-title">Recurrence Timeline — Expected vs. Actual Fire Events</div>
        <div className="pa-panel-sub">One row per active recurrence rule, showing the last 6 expected occurrences against what actually fired in Request Events.</div>
        <div>{v.analyses.map((a) => <TimelineRow key={a.rule.id} a={a} />)}</div>
        <div className="pa-heatmap-legend">
          <div className="pa-legend-item"><span className="pa-legend-swatch" style={{ background: C.green, borderRadius: '50%' }} />Fired on schedule</div>
          <div className="pa-legend-item"><span className="pa-legend-swatch" style={{ background: C.red, borderRadius: '50%' }} />Expected, never fired</div>
          <div className="pa-legend-item"><span className="pa-legend-swatch" style={{ background: 'transparent', border: '2px dashed #c7c7c7', borderRadius: '50%' }} />Not yet due</div>
        </div>
        <div className="pa-chart-note"><b>How to read this:</b> Each dot is one expected occurrence in chronological order. Green = a request event fired on schedule. Red = that occurrence's date has passed with no matching request event — a silent miss, the failure pattern reported for CR260227010. The vertical line marks today.</div>
      </div>

      <div className="pa-panel">
        <div className="pa-panel-title">Active Recurrences Requiring Attention</div>
        <div className="pa-panel-sub">Sorted by severity, then by how many consecutive occurrences have been missed.</div>
        {v.missing.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--pa-text-light)', fontSize: 12 }}>No recurrences currently require attention.</div>
        ) : v.missing.map((a) => (
          <div className="pa-recur-row missed" key={a.rule.id}>
            <span className={`pa-severity-dot ${a.severity}`} />
            <div className="pa-recur-row-main">
              <div className="pa-recur-row-id">{a.rule.id} <span className={`pa-pill ${a.severity === 'critical' ? 'red' : 'amber'}`}>{a.severity === 'critical' ? 'Critical' : 'Warning'}</span></div>
              <div className="pa-recur-row-client">{a.rule.clientName} ({a.rule.client}) · {a.rule.pattern} · Recurrence runs through {fmtDate(a.rule.endDate)}</div>
            </div>
            <div className="pa-recur-row-sched">
              <div className="exp">Expected: {fmtDate(a.lastDue.date)}</div>
              <div className="gap" style={{ color: a.severity === 'critical' ? C.red : C.amber }}>{a.daysSinceShouldHaveFired}d overdue</div>
            </div>
          </div>
        ))}
      </div>

      <div className="pa-panel">
        <div className="pa-panel-title">Full Recurrence Health Table</div>
        <DataTable table={{ cols: ['Client Request ID', 'Client', 'Recurrence Pattern', 'End Date', 'Expected Last Fire', 'Actual Last Fire', 'Status'], rows: tableRows }} />
        <div className="pa-chart-note"><b>How to read this:</b> "Expected Last Fire" is calculated purely from the recurrence rule's own pattern. Compare it against "Actual Last Fire," pulled from real Request Events. Any row where Actual is older than Expected is a confirmed miss.</div>
      </div>
    </>
  );
}

/* ════════════════════ Job TAT Dashboard ════════════════════ */
interface JobTest { name: string; receivedHrsAgo: number; tatHrs: number; }
const JOB_TESTS: Record<string, JobTest[]> = {
  '260612001': [{ name: 'VOC — EPA 524.2', receivedHrsAgo: 7 * 24 + 4, tatHrs: 5 * 24 }, { name: 'Total Metals — EPA 200.8', receivedHrsAgo: 7 * 24 + 4, tatHrs: 7 * 24 }],
  '260613001': [{ name: 'Lead — EPA 200.8', receivedHrsAgo: 6 * 24 + 9, tatHrs: 5 * 24 }],
  '260613002': [{ name: 'Nitrate — EPA 353.2', receivedHrsAgo: 6 * 24 + 6, tatHrs: 6 * 24 }, { name: 'pH / Alkalinity — SM 2320B', receivedHrsAgo: 6 * 24 + 6, tatHrs: 9 * 24 }, { name: 'Total Coliform — SM 9223B', receivedHrsAgo: 6 * 24 + 6, tatHrs: 8 * 24 }, { name: 'TSS — SM 2540D', receivedHrsAgo: 6 * 24 + 6, tatHrs: 10 * 24 }],
  '260618001': [{ name: 'Total Coliform — SM 9223B', receivedHrsAgo: 22, tatHrs: 30 }, { name: 'pH / Alkalinity — SM 2320B', receivedHrsAgo: 22, tatHrs: 5 * 24 }, { name: 'Copper — EPA 200.8', receivedHrsAgo: 22, tatHrs: 7 * 24 }],
  '260616001': [{ name: 'Copper — EPA 200.8', receivedHrsAgo: 2 * 24 + 15, tatHrs: 3 * 24 + 5 }],
  '260615001': [{ name: 'BOD — SM 5210B', receivedHrsAgo: 3 * 24 + 18, tatHrs: 6 * 24 }, { name: 'TSS — SM 2540D', receivedHrsAgo: 3 * 24 + 18, tatHrs: 9 * 24 }],
  '260617001': [{ name: 'PFAS — EPA 537.1', receivedHrsAgo: 1 * 24 + 16, tatHrs: 10 * 24 + 8 }],
};
const JOBS = [
  { id: '260612001', client: 'WCO', clientName: 'Westcoast Water Co.' },
  { id: '260613001', client: 'AQT', clientName: 'Aqua Tech Labs' },
  { id: '260613002', client: 'HHD', clientName: 'Hill Hollow District' },
  { id: '260618001', client: 'BAT', clientName: 'Bay Area Treatment' },
  { id: '260616001', client: 'JEL', clientName: 'Jenkins Environmental' },
  { id: '260615001', client: 'RRA', clientName: 'Rio Reclamation Authority' },
  { id: '260617001', client: 'AQT', clientName: 'Aqua Tech Labs' },
];
type Tier = 'critical' | 'warning' | 'info' | 'ontrack';
const fmtHrs = (hrs: number) => { const sign = hrs < 0 ? '−' : ''; const abs = Math.abs(hrs); const d = Math.floor(abs / 24); const h = Math.round(abs % 24); return d === 0 ? `${sign}${h}h` : `${sign}${d}d ${h}h`; };
const tierFor = (hrsRemaining: number): Tier => hrsRemaining < 0 ? 'critical' : hrsRemaining <= 24 ? 'warning' : hrsRemaining <= 96 ? 'info' : 'ontrack';
function analyzeJob(job: typeof JOBS[number]) {
  const tests = (JOB_TESTS[job.id] ?? []).map((t) => { const hrsRemaining = t.tatHrs - t.receivedHrsAgo; return { ...t, hrsRemaining, tier: tierFor(hrsRemaining) }; });
  const bottleneck = tests.reduce((w, t) => (t.hrsRemaining < w.hrsRemaining ? t : w), tests[0]!);
  const rank: Record<Tier, number> = { critical: 0, warning: 1, info: 2, ontrack: 3 };
  const jobTier = tests.reduce<Tier>((w, t) => (rank[t.tier] < rank[w] ? t.tier : w), 'ontrack');
  const overdueCount = tests.filter((t) => t.tier === 'critical').length;
  const dueSoonCount = tests.filter((t) => t.tier === 'warning').length;
  return { job, tests, bottleneck, jobTier, overdueCount, dueSoonCount };
}

export function JobTatPage() {
  const [open, setOpen] = useState<Set<string>>(new Set());
  const analyses = useMemo(() => {
    const rank: Record<Tier, number> = { critical: 0, warning: 1, info: 2, ontrack: 3 };
    return JOBS.map(analyzeJob).sort((a, b) => rank[a.jobTier] - rank[b.jobTier] || a.bottleneck.hrsRemaining - b.bottleneck.hrsRemaining);
  }, []);
  const overdue = analyses.filter((a) => a.jobTier === 'critical').length;
  const warn = analyses.filter((a) => a.jobTier === 'warning').length;
  const info = analyses.filter((a) => a.jobTier === 'info').length;
  const toggle = (id: string) => setOpen((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <>
      <PageHead title="Job TAT Dashboard" badge="new"
        desc="Open jobs by turnaround urgency. A job's due date is the earliest due test inside it, and its urgency tier is driven by its single worst test — a job with 9 tests on track and 1 overdue still shows as overdue, because the whole job can't report out until everything inside it is done." />
      <KpiRowN kpis={[
        { label: 'Jobs Overdue', value: String(overdue), valueColor: C.red, delta: 'At least one test inside is past due', dir: 'down' },
        { label: 'Jobs Due Within 24hr', value: String(warn), valueColor: '#b9750f', delta: 'Worst test in each job', dir: 'flat' },
        { label: 'Jobs Due 2–4 Days', value: String(info), valueColor: C.blue, delta: 'On track', dir: 'flat' },
        { label: 'Jobs On Time · 30 Days', value: '91.6%', delta: '▼ 1.2pt vs prior month', dir: 'down' },
      ]} />
      <div className="pa-panel">
        <div className="pa-panel-title">Open Jobs — Sorted by Urgency</div>
        <div className="pa-panel-sub">Click a job row to expand its component tests and see which one is the bottleneck.</div>
        {analyses.map((a) => {
          const isOpen = open.has(a.job.id);
          return (
            <div key={a.job.id}>
              <div className={`pa-job-row${isOpen ? ' expanded' : ''}`} onClick={() => toggle(a.job.id)}>
                <span className="pa-job-chev">▶</span>
                <span className={`pa-urg-bar ${a.jobTier}`} />
                <div className="pa-job-main">
                  <div className="pa-job-id">{a.job.id} <span style={{ color: 'var(--pa-text-light)', fontWeight: 'normal' }}>— {a.job.clientName} ({a.job.client})</span></div>
                  <div className="pa-job-meta">{a.tests.length} test{a.tests.length > 1 ? 's' : ''}
                    {a.overdueCount > 0 ? <span style={{ color: C.red, fontWeight: 'bold' }}> · {a.overdueCount} overdue</span>
                      : a.dueSoonCount > 0 ? <span style={{ color: '#b9750f', fontWeight: 'bold' }}> · {a.dueSoonCount} due soon</span> : null}
                    {' '}&nbsp;•&nbsp; driven by <b>{a.bottleneck.name.split(' — ')[0]}</b></div>
                </div>
                <div className="pa-job-due">
                  <div className={`pa-time-rem ${a.jobTier}`}>{a.bottleneck.hrsRemaining < 0 ? `${fmtHrs(a.bottleneck.hrsRemaining)} overdue` : `${fmtHrs(a.bottleneck.hrsRemaining)} remaining`}</div>
                  <div className="pa-job-tag">{a.jobTier === 'critical' || a.jobTier === 'warning' ? <span className="pa-alert-fired">🔔 Fired</span> : <span className="pa-alert-pending">—</span>}</div>
                </div>
              </div>
              {isOpen && (
                <div className="pa-job-test-list open">
                  {a.tests.map((t, i) => (
                    <div className="pa-job-test-row" key={i}>
                      <span className={`pa-urg-bar small ${t.tier}`} />
                      <span className="pa-test-name">↳ {t.name}</span>
                      <span className="pa-test-due">
                        <span className={`pa-time-rem ${t.tier}`}>{t.hrsRemaining < 0 ? `${fmtHrs(t.hrsRemaining)} overdue` : `${fmtHrs(t.hrsRemaining)} remaining`}</span>
                        {t === a.bottleneck && <span className="pa-bottleneck-pill">Bottleneck</span>}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        <div className="pa-heatmap-legend">
          <div className="pa-legend-item"><span className="pa-legend-swatch" style={{ background: C.red }} />Overdue — at least one test past due</div>
          <div className="pa-legend-item"><span className="pa-legend-swatch" style={{ background: C.amber }} />Due within 24 hours</div>
          <div className="pa-legend-item"><span className="pa-legend-swatch" style={{ background: C.blue }} />Due within 2–4 days</div>
          <div className="pa-legend-item"><span className="pa-legend-swatch" style={{ background: C.green }} />On track — more than 4 days remaining</div>
        </div>
      </div>
    </>
  );
}

/* ════════════════════ Due Date Tracking ════════════════════ */
export function DueDatePage() {
  const methods = [
    { name: 'EPA 524.2 — VOC', basis: 'Received date + business days', defaultTat: '5 business days', overrides: 'WCO: 3 days', status: 'ok' },
    { name: 'EPA 200.8 — Metals (Lead, Copper)', basis: 'Received date + business days', defaultTat: '7 business days', overrides: 'AQT: 5 days', status: 'ok' },
    { name: 'SM 9223B — Total Coliform / E. coli', basis: 'Collection date + 30hr (holding time driven)', defaultTat: '30 hours', overrides: '—', status: 'ok' },
    { name: 'EPA 537.1 — PFAS Extended Panel', basis: 'Not configured', defaultTat: '—', overrides: '—', status: 'missing' },
    { name: 'SM 2540D — Total Suspended Solids', basis: 'Received date + business days', defaultTat: '3 business days', overrides: 'BAT: 2 days', status: 'ok' },
    { name: 'SM 5210B — BOD', basis: 'Received date + business days', defaultTat: '5 business days', overrides: '—', status: 'error' },
  ];
  const missingCount = methods.filter((m) => m.status === 'missing').length;
  const rows: Cell[][] = methods.map((m) => {
    const statusCell: Cell = m.status === 'ok' ? { text: '✓ OK', pill: 'green' }
      : m.status === 'missing' ? { text: '⚠ Missing TAT definition', pill: 'amber' }
        : { text: '✕ 3 samples missing received-date', pill: 'red' };
    return [m.name, m.basis, m.defaultTat, m.overrides, statusCell];
  });
  return (
    <>
      <PageHead title="Due Date Tracking" badge="new"
        desc="How TAT due dates are calculated and verified, per method. This is the data layer underneath the Job TAT Dashboard — if a method has no TAT definition, or a sample is missing its received-date timestamp, the affected job silently falls through the dashboard above without anyone noticing." />
      <KpiRowN kpis={[
        { label: 'Methods With TAT Defined', value: '118', delta: 'of 124 active methods', dir: 'flat' },
        { label: 'Missing TAT Definition', value: String(missingCount), valueColor: '#b9750f', delta: 'Due date cannot be calculated', dir: 'down' },
        { label: 'Samples With No Due Date', value: '3', valueColor: C.red, delta: 'Falls through TAT dashboard silently', dir: 'down' },
        { label: 'Client-Specific Overrides', value: '14', valueColor: C.blue, delta: 'Contractual TAT shorter than default', dir: 'flat' },
      ]} />
      <div className="pa-recur-banner"><div className="pa-recur-banner-icon">⚠️</div>
        <div className="pa-recur-banner-text"><b>3 samples currently have no due date calculated.</b> This is the most common reason a job silently misses the Job TAT Dashboard. Caused by either a missing TAT definition on the method (see EPA 537.1 below), or a missing received-date timestamp (see SM 5210B below).</div></div>
      <div className="pa-panel">
        <div className="pa-panel-title">Method TAT Configuration</div>
        <div className="pa-panel-sub">Default TAT basis per method, with any client-specific contractual overrides.</div>
        <DataTable table={{ cols: ['Method', 'TAT Basis', 'Default TAT', 'Client Overrides', 'Calculation Status'], rows }} />
        <div className="pa-chart-note"><b>How to read this:</b> "OK" means the method has a TAT definition and every open sample on it has a valid received-date timestamp. Anything else is a method that needs configuration before it can reliably feed the Job TAT Dashboard.</div>
      </div>
    </>
  );
}

/* ════════════════════ Missed TAT Alerts ════════════════════ */
export function TatAlertsPage() {
  const rules = [
    { name: 'TAT Breached — Job Overdue', code: 'PHASE1-TAT-BREACH', severity: 'red' as const, condition: 'MIN(test.hrsRemaining across job.tests) < 0', recipients: 'Lab Manager, Analyst', ack: true },
    { name: 'TAT Approaching — 24hr Warning', code: 'PHASE1-TAT-WARN', severity: 'amber' as const, condition: 'MIN(test.hrsRemaining across job.tests) <= 24', recipients: 'Lab Manager', ack: false },
  ];
  const firings = [
    { jobId: '260612001', client: 'WCO', rule: 'PHASE1-TAT-BREACH', detail: 'VOC test −2d 4h overdue', when: '8 min ago', status: 'New' },
    { jobId: '260613001', client: 'AQT', rule: 'PHASE1-TAT-BREACH', detail: 'Lead test −1d 9h overdue', when: '26 min ago', status: 'New' },
    { jobId: '260613002', client: 'HHD', rule: 'PHASE1-TAT-BREACH', detail: 'Nitrate test −6h overdue', when: '1 hr ago', status: 'Acknowledged · Wilson' },
    { jobId: '260618001', client: 'BAT', rule: 'PHASE1-TAT-WARN', detail: 'Total Coliform due in 8h', when: '3 hr ago', status: 'New' },
    { jobId: '260616001', client: 'JEL', rule: 'PHASE1-TAT-WARN', detail: 'Copper due in 19h', when: '5 hr ago', status: 'Not yet sent' },
  ];
  const ruleRows: Cell[][] = rules.map((r) => [r.name, r.code,
    { text: r.severity === 'red' ? 'Critical' : 'Warning', pill: r.severity } as Cell,
    r.condition, r.recipients, r.ack ? '✓' : '—']);
  return (
    <>
      <PageHead title="Missed TAT Alerts" badge="new"
        desc="Notification rules that watch job-level TAT state and alert a human before — or as soon as — a job breaches its due date. This is the delivery layer on top of the Job TAT Dashboard and Due Date Tracking: the data already exists, this is what proactively tells someone instead of waiting for them to check." />
      <KpiRowN kpis={[
        { label: 'Active Rules', value: '2', delta: 'Job-level, not per-test', dir: 'flat' },
        { label: 'Fired · 7 Days', value: '17', delta: 'Across both rules', dir: 'down' },
        { label: 'Currently Open', value: '3', valueColor: C.red, delta: 'Awaiting acknowledgment', dir: 'down' },
        { label: 'Avg Time to Acknowledge', value: '18 min', delta: '▲ faster vs prior month', dir: 'up' },
      ]} />
      <div className="pa-panel">
        <div className="pa-panel-title">Active TAT Alert Rules</div>
        <div className="pa-panel-sub">Job-level rules — fire once per job, not once per test, even when multiple tests inside a job are at risk.</div>
        <DataTable table={{ cols: ['Rule', 'Rule Code', 'Severity', 'Condition', 'Recipients', 'Ack Required'], rows: ruleRows }} />
      </div>
      <div className="pa-panel">
        <div className="pa-panel-title">Recent Firings</div>
        <div className="pa-panel-sub">Notifications actually generated by the rules above, most recent first.</div>
        {firings.map((f, i) => (
          <div className="pa-recur-row" key={i}>
            <span className={`pa-severity-dot ${f.rule === 'PHASE1-TAT-BREACH' ? 'critical' : 'warn'}`} />
            <div className="pa-recur-row-main">
              <div className="pa-recur-row-id">{f.jobId} <span className={`pa-pill ${f.rule === 'PHASE1-TAT-BREACH' ? 'red' : 'amber'}`}>{f.rule}</span></div>
              <div className="pa-recur-row-client">{f.client} · {f.detail}</div>
            </div>
            <div className="pa-recur-row-sched"><div className="exp">{f.when}</div><div className="gap" style={{ color: 'var(--pa-text-mid)' }}>{f.status}</div></div>
          </div>
        ))}
      </div>
    </>
  );
}

/* ════════════════════ Revenue Explorer ════════════════════ */
interface DimCfg { labels: string[]; color: string; seedBase: number; niceLabel?: (s: string) => string; }
const EXPLORER_DIMENSIONS: Record<string, DimCfg> = {
  client: { labels: [...CLIENTS], color: C.orange, seedBase: 700, niceLabel: (c) => CLIENT_NAMES[c] ?? c },
  test: { labels: TESTS, color: C.blue, seedBase: 710 },
  dept: { labels: DEPARTMENTS, color: C.green, seedBase: 720 },
  method: { labels: TESTS, color: '#7F77DD', seedBase: 730 },
  matrix: { labels: MATRICES, color: C.blue, seedBase: 740, niceLabel: (m) => MATRIX_NAMES[m] ?? m },
  program: { labels: PROGRAMS, color: '#D85A30', seedBase: 745 },
  sampletype: { labels: SAMPLE_TYPES, color: C.amber, seedBase: 750 },
  rep: { labels: REPS, color: C.green, seedBase: 760 },
  project: { labels: PROJECTS, color: C.red, seedBase: 770 },
};
const DIM_LABELS: Record<string, string> = { client: 'Client', test: 'Test Code', dept: 'Department', method: 'Method', matrix: 'Matrix', program: 'Regulatory Program', sampletype: 'Sample Type', rep: 'Sales Rep', project: 'Project' };
const PERIOD_LABELS: Record<string, string> = { ttm: 'Trailing 12 Months', qtd: 'Quarter to Date', mtd: 'Month to Date', ytd: 'Year to Date' };
const PERIOD_SCALE: Record<string, number> = { ttm: 1, qtd: 0.25, mtd: 0.083, ytd: 0.5 };

export function RevenueExplorerPage() {
  const [dim, setDim] = useState('client');
  const [period, setPeriod] = useState('ttm');
  const [compare, setCompare] = useState('none');
  const [sort, setSort] = useState('desc');

  const v = useMemo(() => {
    const cfg = EXPLORER_DIMENSIONS[dim]!; const scale = PERIOD_SCALE[period]!;
    let rows = cfg.labels.map((label, i) => ({
      label: cfg.niceLabel ? cfg.niceLabel(label) : label, raw: label,
      revenue: Math.round(one(240000, 110000, cfg.seedBase + i) * scale),
      prior: Math.round(one(240000, 110000, cfg.seedBase + i + 50) * scale),
    }));
    if (sort === 'desc') rows = rows.sort((a, b) => b.revenue - a.revenue);
    else if (sort === 'asc') rows = rows.sort((a, b) => a.revenue - b.revenue);
    else rows = rows.sort((a, b) => a.label.localeCompare(b.label));
    const total = rows.reduce((s, r) => s + r.revenue, 0);
    const totalPrior = rows.reduce((s, r) => s + r.prior, 0);
    const pctChange = totalPrior > 0 ? ((total - totalPrior) / totalPrior * 100) : 0;
    const top = rows.reduce((b, r) => (r.revenue > b.revenue ? r : b), rows[0]!);
    const topShare = total > 0 ? (top.revenue / total * 100) : 0;
    return { cfg, rows, total, pctChange, top, topShare };
  }, [dim, period, compare, sort]);

  const dimLabel = DIM_LABELS[dim]!;
  const showCompare = compare !== 'none';
  const compareLabel = compare === 'prior' ? 'Prior Period' : 'Same Period Last Year';
  const tableCols = ['', 'Revenue', '% of Total', ...(showCompare ? [compareLabel, 'Change'] : [])];
  tableCols[0] = dimLabel;
  const tableRows: Cell[][] = v.rows.map((r) => {
    const share = v.total > 0 ? (r.revenue / v.total * 100) : 0;
    const change = r.prior > 0 ? ((r.revenue - r.prior) / r.prior * 100) : 0;
    const base: Cell[] = [r.label, `$${(r.revenue / 1000).toFixed(0)}K`, `${share.toFixed(1)}%`];
    if (showCompare) base.push(`$${(r.prior / 1000).toFixed(0)}K`, { text: `${change >= 0 ? '▲' : '▼'} ${Math.abs(change).toFixed(1)}%`, pill: change >= 0 ? 'green' : 'red' } as Cell);
    return base;
  });

  const Select = ({ label, value, set, opts }: { label: string; value: string; set: (v: string) => void; opts: [string, string][] }) => (
    <div className="pa-expl-control"><label>{label}</label>
      <select value={value} onChange={(e) => set(e.target.value)}>{opts.map(([v2, l]) => <option key={v2} value={v2}>{l}</option>)}</select></div>
  );

  return (
    <>
      <PageHead title="Revenue Explorer" badge="new"
        desc="One revenue dataset, sliced by whichever dimension you need. Covers Revenue by Client, Test Code, Department, Method, Matrix, Sample Type, and Sales Rep — pick a dimension instead of hunting for a separate dashboard per slice." />
      <div className="pa-explorer-controls">
        <Select label="Slice by" value={dim} set={setDim} opts={Object.keys(EXPLORER_DIMENSIONS).map((k) => [k, DIM_LABELS[k]!])} />
        <Select label="Period" value={period} set={setPeriod} opts={[['ttm', 'Trailing 12 Months'], ['qtd', 'Quarter to Date'], ['mtd', 'Month to Date'], ['ytd', 'Year to Date']]} />
        <Select label="Compare" value={compare} set={setCompare} opts={[['none', 'No comparison'], ['prior', 'Vs. prior period'], ['yoy', 'Vs. same period last year']]} />
        <Select label="Sort" value={sort} set={setSort} opts={[['desc', 'Highest revenue first'], ['asc', 'Lowest revenue first'], ['alpha', 'Alphabetical']]} />
      </div>
      <KpiRowN kpis={[
        { label: 'Total Revenue', value: `$${(v.total / 1000).toFixed(0)}K`, delta: `${v.pctChange >= 0 ? '▲' : '▼'} ${Math.abs(v.pctChange).toFixed(1)}%`, dir: v.pctChange >= 0 ? 'up' : 'down' },
        { label: `Top ${dimLabel}`, value: v.top.label, delta: `$${(v.top.revenue / 1000).toFixed(0)}K (${v.topShare.toFixed(0)}%)`, dir: 'flat' },
        { label: `${dimLabel} Count`, value: String(v.rows.length), delta: '— active', dir: 'flat' },
        { label: `Avg per ${dimLabel}`, value: `$${(v.total / v.rows.length / 1000).toFixed(0)}K`, delta: '— mean', dir: 'flat' },
      ]} />
      <div className="pa-panel">
        <div className="pa-panel-title">Revenue by {dimLabel}</div>
        <div className="pa-panel-sub">{PERIOD_LABELS[period]}{showCompare ? `, compared to ${compare === 'prior' ? 'prior period' : 'same period last year'}` : ''}</div>
        <div className="pa-chart-wrap tall">
          {showCompare ? (
            <PAChart spec={{ type: 'groupBar', horizontal: true, labels: v.rows.map((r) => r.label),
              series: [{ name: 'Revenue', color: v.cfg.color, data: v.rows.map((r) => r.revenue) }, { name: compareLabel, color: 'rgba(0,0,0,0.25)', data: v.rows.map((r) => r.prior) }] }} />
          ) : (
            <PAChart spec={{ type: 'bar', horizontal: true, labels: v.rows.map((r) => r.label), data: v.rows.map((r) => r.revenue), color: v.cfg.color }} />
          )}
        </div>
      </div>
      <div className="pa-panel">
        <div className="pa-panel-title">Full Breakdown</div>
        <DataTable table={{ cols: tableCols, rows: tableRows }} />
      </div>
    </>
  );
}

/* ════════════════════ Revenue Trend & Forecast ════════════════════ */
export function RevenueTrendPage() {
  const v = useMemo(() => {
    const actualMonths = 9;
    const all = series(12, 170000, 35000, 800);
    const actual = all.slice(0, actualMonths);
    const lastActual = actual[actual.length - 1]!;
    const avgGrowth = (actual[actual.length - 1]! - actual[0]!) / actual.length;
    const forecast: number[] = [];
    for (let i = actualMonths; i < 12; i++) forecast.push(Math.round(lastActual + avgGrowth * (i - actualMonths + 1)));
    const actualLine: (number | null)[] = [...actual, ...Array<null>(12 - actualMonths).fill(null)];
    const forecastLine: (number | null)[] = [...Array<null>(actualMonths - 1).fill(null), lastActual, ...forecast];
    return { actualMonths, actualLine, forecastLine };
  }, []);
  return (
    <>
      <PageHead title="Revenue Trend & Forecast" badge="new"
        desc='Monthly revenue over time against a simple linear forecast, plus the recurring-vs-one-time split. This is genuinely time-series shaped — it answers "are we meeting revenue goals," which a dimensional breakdown can&apos;t.' />
      <KpiRowN kpis={[
        { label: 'Revenue (TTM)', value: '$2.10M', delta: '▲ 8.1% vs prior TTM', dir: 'up' },
        { label: 'This Month (Actual)', value: '$187K', delta: '▲ 4.2% vs forecast', dir: 'up' },
        { label: 'Next Month (Forecast)', value: '$191K', delta: '— trend-projected', dir: 'flat' },
        { label: 'Recurring Share', value: '74%', delta: '▲ 2pp vs prior year', dir: 'up' },
      ]} />
      <ChartPanel panel={{ title: 'Monthly Revenue — Actual vs Forecast', tall: true,
        chart: { type: 'line', labels: MONTHS, legend: true, series: [
          { name: 'Actual', color: C.orange, data: v.actualLine },
          { name: 'Forecast', color: C.blue, dashed: true, data: v.forecastLine },
        ] },
        note: 'Solid line is actual revenue booked; dashed is a trend-based forecast for months not yet closed. A gap between them in a closed month is forecast error worth investigating.' }} />
      <div className="pa-panel-row pa-cols-2e">
        <ChartPanel panel={{ title: 'Recurring vs One-Time',
          chart: { type: 'pie', donut: true, legendPos: 'bottom', labels: ['Recurring', 'One-Time'], data: [74, 26], colors: [C.green, C.grey] },
          note: 'Recurring is contracted/scheduled testing programs; one-time is project work. A growing recurring share means more predictable revenue.' }} />
        <ChartPanel panel={{ title: 'New vs Existing Client Revenue',
          chart: { type: 'groupBar', stacked: true, labels: MONTHS.slice(0, 9), series: [
            { name: 'Existing Clients', color: C.orange, data: series(9, 140000, 20000, 810) },
            { name: 'New Clients', color: C.blueLight, data: series(9, 22000, 12000, 820).map((x) => Math.max(2000, x)) },
          ] },
          note: 'Existing-client revenue growing faster than new-client revenue usually means expansion within accounts; the reverse means growth is acquisition-driven.' }} />
      </div>
    </>
  );
}

/* ════════════════════ Top Clients & Concentration Risk ════════════════════ */
export function RevenueConcentrationPage() {
  const v = useMemo(() => {
    const rows = CLIENTS.map((c) => ({ client: c, name: CLIENT_NAMES[c]!, revenue: Math.round(2100000 * (CLIENT_REVENUE_WEIGHT[c] ?? 0)) }))
      .sort((a, b) => b.revenue - a.revenue);
    const total = rows.reduce((s, r) => s + r.revenue, 0);
    const top5 = rows.slice(0, 5);
    return { rows, total, top5 };
  }, []);
  const top5Colors = [C.orange, C.blue, C.green, C.amber, C.red];
  return (
    <>
      <PageHead title="Top Clients & Concentration Risk" badge="new"
        desc="Ranking and risk-shaped, not a dimensional slice: which clients matter most, and how exposed is the business if one of them leaves." />
      <KpiRowN kpis={[
        { label: 'Top Client', value: 'DWO', delta: '$498K TTM, 23.7%', dir: 'flat' },
        { label: 'Top 3 Concentration', value: '52%', delta: 'elevated risk', dir: 'down' },
        { label: 'Top 10 Concentration', value: '91%', delta: 'very concentrated client base', dir: 'down' },
        { label: 'Clients >15% of Revenue', value: '2', delta: 'DWO, AQT', dir: 'down' },
      ]} />
      <ChartPanel panel={{ title: 'Top 10 Clients by TTM Revenue', tall: true,
        chart: { type: 'bar', horizontal: true, labels: v.rows.map((r) => r.client),
          data: v.rows.map((r) => Math.round(r.revenue / v.total * 1000) / 10),
          color: v.rows.map((r) => (r.revenue / v.total * 100) > 15 ? C.red : C.orange) },
        note: 'Bars sorted descending, shown as % of total revenue. Anything above ~15% represents meaningful exposure if that relationship weakens.' }} />
      <ChartPanel panel={{ title: 'Client Revenue Trend — Top 5', tall: true,
        chart: { type: 'line', labels: MONTHS, legend: true, series: v.top5.map((r, i) => ({ name: r.client, color: top5Colors[i]!, data: series(12, r.revenue / 12, r.revenue / 12 * 0.3, 900 + i) })) },
        note: 'Monthly revenue for the 5 largest clients. A top client trending down is worth a conversation well before it shows up as full churn.' }} />
    </>
  );
}
